import { FileText, Plus, Trash2, Tag, CheckCircle, ExternalLink, Upload } from 'lucide-react';
import CustomInput from '../../../../../../../components/forms/CustomInput';

const ImportantDocsSection = ({
  formData,
  addImportantDocument,
  updateImportantDocumentName,
  handleImportantDocUpload,
  removeImportantDocument
}) => {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 md:p-8 shadow-sm">
      <div className="flex items-center justify-between gap-2.5 sm:gap-4 mb-4 sm:mb-6">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-7 h-7 sm:w-10 sm:h-10 shrink-0 bg-teal-50 text-primary rounded-lg sm:rounded-xl flex items-center justify-center shadow-inner">
            <FileText size={14} className="sm:w-5 sm:h-5" />
          </div>
          <div>
            <h3 className="text-xs sm:text-base font-semibold sm:font-bold text-slate-800 tracking-wider">Important Documents</h3>
            <p className="hidden sm:block text-xs text-slate-500 font-normal sm:font-medium mt-0.5">Upload tickets, vouchers, IDs, or other travel documents</p>
          </div>
        </div>
        <button
          onClick={addImportantDocument}
          className="flex items-center gap-1.5 sm:gap-2 px-2.5 py-1 sm:px-4 sm:py-2 bg-primary text-white rounded-lg sm:rounded-xl font-medium sm:font-semibold text-[11px] sm:text-xs hover:opacity-95 transition-all active:scale-95 shadow-sm shrink-0 cursor-pointer"
          type="button"
        >
          <Plus size={12} className="sm:w-3.5 sm:h-3.5" />
          <span>
            <span className="sm:hidden">Add</span>
            <span className="hidden sm:inline">Add Document</span>
          </span>
        </button>
      </div>

      {(!formData.important_documents || formData.important_documents.length === 0) ? (
        <div className="p-8 text-center bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
          <FileText size={32} className="mx-auto text-slate-300 mb-2" />
          <p className="text-xs font-semibold text-slate-500">No important documents added yet.</p>
          <p className="text-[11px] text-slate-400 mt-1">Click "+ Add Document" above to attach tickets, IDs, or vouchers.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {formData.important_documents.map((doc, idx) => (
            <div
              key={doc.id || doc._id || idx}
              id={`doc-${idx}`}
              className="p-6 bg-white border border-slate-200 rounded-3xl relative transition-all duration-300 shadow-sm hover:shadow-md hover:border-slate-300 group mb-4"
            >
              <div className="flex items-center justify-between gap-2 mb-4 pb-3 border-b border-slate-100">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-black tracking-wider uppercase shrink-0">
                  Document #{idx + 1}
                </span>
                <button
                  onClick={() => removeImportantDocument(idx)}
                  className="flex items-center gap-1.5 px-3 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors font-bold text-xs shadow-sm cursor-pointer"
                  title="Remove Document"
                  type="button"
                >
                  <Trash2 size={14} /> <span className="hidden sm:inline">Remove</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Document Name */}
                <div>
                  <CustomInput
                    label="Name of Document *"
                    icon={Tag}
                    value={doc.name || ''}
                    onChange={(e) => updateImportantDocumentName(idx, e.target.value)}
                    placeholder="e.g. Flight Ticket, Passport Copy, Insurance"
                  />
                </div>

                {/* Document Upload Box */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 tracking-wide mb-1.5">
                    Document File
                  </label>

                  {doc.isUploading ? (
                    <div className="flex items-center justify-center gap-3 p-3.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-primary">
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                      Uploading document...
                    </div>
                  ) : doc.url ? (
                    <div className="flex items-center justify-between p-3 bg-emerald-50/50 border border-emerald-200 rounded-xl">
                      <div className="flex items-center gap-2 overflow-hidden">
                        <CheckCircle size={18} className="text-emerald-600 shrink-0" />
                        <span className="text-xs font-bold text-slate-700 truncate max-w-[180px]">
                          {doc.filename || doc.name || 'Uploaded File'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-2.5 py-1.5 bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-100 rounded-lg text-xs font-bold transition-all shadow-sm"
                        >
                          <ExternalLink size={12} /> View
                        </a>
                        <label className="cursor-pointer px-2.5 py-1.5 bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg text-xs font-bold transition-all shadow-sm">
                          Change
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*,.pdf,.doc,.docx"
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                handleImportantDocUpload(idx, e.target.files[0]);
                              }
                            }}
                          />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center p-4 bg-slate-50 hover:bg-slate-100/80 border-2 border-dashed border-slate-200 hover:border-primary/50 rounded-xl cursor-pointer transition-all text-center group/up">
                      <Upload size={20} className="text-slate-400 group-hover/up:text-primary transition-colors mb-1" />
                      <span className="text-xs font-bold text-slate-600 group-hover/up:text-primary transition-colors">
                        Click to upload document
                      </span>
                      <span className="text-[10px] text-slate-400 mt-0.5">
                        Supports PDF, Images, DOC, DOCX
                      </span>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*,.pdf,.doc,.docx"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            handleImportantDocUpload(idx, e.target.files[0]);
                          }
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImportantDocsSection;
